/**
 * A fake server for the community pages, dev builds only.
 *
 * The real API needs a signed-in session, which the dev preview cannot have
 * (the auth gate runs before the router). Under /dev/* the gate is skipped
 * (main.tsx) and this module wraps `fetch` with an in-memory dataset shaped
 * like the prototype's seed data, so every page and every mutation can be
 * exercised and compared against the bundle without an account. Nothing here
 * ships: the router only mounts /dev/* when import.meta.env.DEV.
 */
import type { Collection, CollectionDetail, CollectionTarget, PlacedStudy, ServerVisibility, StudyGroup } from '../../features/collections/api';
import { LATERALITY_CODES, certainKind, isImageType, isLaterality, modalityOf, typeFits, type ImageKind, type ImageType } from '../../data/imageTypes';
import { STAIN_SCHEME } from '../data/codes';

declare global {
  interface Window {
    __TR_FIXTURES?: true;
  }
}

export const HE = 'linear-gradient(135deg,#efc4d3,#c67f9b 50%,#8a5375)';
export const IHC = 'linear-gradient(135deg,#dcb896,#b07a4d 55%,#6e4426)';
export const PAS = 'linear-gradient(135deg,#e9c6e0,#b982b8 55%,#6f4a7d)';
export const TRI = 'linear-gradient(135deg,#bcd4e6,#7a9fc2 55%,#3f5f86)';
export const GMS = 'linear-gradient(135deg,#d7d7cf,#9c9c92 55%,#4f4f48)';

let seq = 1;
const uid = () => `1.2.826.0.1.3680043.8.498.${Date.now() % 100000}${seq++}`;

interface FxStudy extends PlacedStudy {
  cover: string;
  /** The head's demographics, for the details editor. */
  sex?: 'M' | 'F' | 'O' | null;
  /** DICOM AS, e.g. `054Y`. */
  age?: string | null;
  /** `YYYY-MM-DD`. */
  study_date?: string | null;
}
interface FxCollection extends Omit<Collection, 'covers' | 'studies' | 'groups' | 'images'> {
  groups: StudyGroup[];
  studies: FxStudy[];
}

/** Covers by public id: a study's (`stu_…`), or an image's (`img_…`), which wears its study's. */
const COVERS: Record<string, string> = {};
export function fixtureCover(publicId: string): string | undefined {
  return COVERS[publicId];
}

const hex12 = (n: number) => n.toString(16).padStart(12, '0');

/** The fake image `i` of a study, by public id; its cover is registered as it is named. */
function imageId(st: FxStudy, i: number): string {
  const id = `img_${hex12(parseInt(st.public_id.slice(4), 16) * 16 + i)}`;
  COVERS[id] = st.cover;
  return id;
}

function study(name: string, desc: string, group: string | null, cover: string, images: number, date: string, extra: Partial<Omit<FxStudy, 'cover'>> = {}): FxStudy {
  const publicId = `stu_${hex12(seq)}`;
  COVERS[publicId] = cover;
  return {
    cover,
    public_id: publicId,
    study_instance_uid: uid(),
    ordinal: String(seq),
    group_public_id: group,
    study_id: name,
    description: desc,
    diagnosis: desc.split(' · ')[0],
    visibility: 'workspace',
    visibility_own: 'workspace',
    like_count: (name.length * 7 + images * 3) % 41,
    comment_count: 0,
    liked: false,
    images,
    unpublished: 0,
    created_utc: new Date(date).toISOString(),
    ...extra,
  };
}

function group(name: string, description: string): StudyGroup {
  return { public_id: `grp_${(seq++).toString(16).padStart(12, '0')}`, name, description, ordinal: String(seq) };
}

const g1 = group('Receptor scoring (teaching)', 'Clear-cut ER/PR/HER2 examples for scoring practice.');
const g2 = group('HER2 equivocal', 'HER2 2+ cases pending or with FISH results.');
const g3 = group('Ki-67 borderline', 'Ki-67 in the 10 to 20% range where scoring is hardest.');
const r1 = group('Glomerular disease', 'Native biopsies with glomerulonephritis patterns.');
const r2 = group('Transplant', 'Allograft biopsies: rejection and BK nephropathy.');

const DATA: FxCollection[] = [
  {
    public_id: 'col_000000000001', name: 'Breast IHC panel', visibility: 'workspace', created_by: 'u1', author: 'Ana Alvarez',
    description: '<p>Teaching set of breast carcinomas with the full <strong>ER, PR, HER2 and Ki-67</strong> panel on every case, scored by two pathologists.</p><h2>Inclusion criteria</h2><ul><li>Core needle biopsies from 2019 onwards</li><li>Complete receptor panel, no missing blocks</li><li>Consent for teaching use on file</li></ul>',
    created_utc: '2026-08-01T10:00:00Z', modified_utc: '2026-09-01T10:00:00Z', like_count: 128, comment_count: 9, liked: false,
    groups: [g1, g2, g3],
    studies: [
      study('IDC-01', 'Invasive ductal carcinoma, grade 2 · ER+ PR+ HER2 1+ · Ki-67 18%', null, HE, 5, '2026-08-30', { comment_count: 4 }),
      study('IDC-02', 'Invasive ductal carcinoma with DCIS · ER− · HER2 3+', null, HE, 4, '2026-08-28', { visibility: 'unlisted', visibility_own: 'unlisted' }),
      study('IDC-03', 'Invasive lobular carcinoma, E-cadherin negative', null, HE, 6, '2026-08-27'),
      study('IDC-04', 'Triple-negative, high grade · Ki-67 72%', g1.public_id, IHC, 5, '2026-08-27'),
      study('IDC-05', 'Mucinous carcinoma · ER+ PR+ HER2 0', g1.public_id, HE, 5, '2026-08-25'),
      study('IDC-06', 'IDC with extensive DCIS, comedo type', g1.public_id, HE, 6, '2026-08-24'),
      study('IDC-07', 'Invasive ductal carcinoma, grade 2 · reference for the panel', g1.public_id, HE, 5, '2026-08-22'),
      study('HER2-E1', 'HER2 2+ by IHC, FISH pending', g2.public_id, HE, 3, '2026-08-20'),
      study('HER2-E2', 'HER2 2+ heterogeneous', g2.public_id, HE, 3, '2026-08-19'),
      study('KI-B1', 'Ki-67 ~14%, borderline', g3.public_id, HE, 2, '2026-08-18'),
    ],
  },
  {
    public_id: 'col_000000000002', name: 'Renal biopsies 2026', visibility: 'unlisted', created_by: 'u1', author: 'Ana Alvarez',
    description: '<p>Native and transplant kidney biopsies with PAS, trichrome and silver stains.</p>',
    created_utc: '2026-08-01T10:00:00Z', modified_utc: '2026-09-02T10:00:00Z', like_count: 64, comment_count: 8, liked: false,
    groups: [r1, r2],
    studies: [
      study('RB-118', 'Membranous nephropathy, stage II', r1.public_id, PAS, 3, '2026-09-02', { visibility: 'unlisted' }),
      study('RB-117', 'IgA nephropathy, M1 E0 S1 T0', r1.public_id, PAS, 3, '2026-09-01', { visibility: 'unlisted' }),
      study('RB-115', 'Acute T-cell mediated rejection, Banff 1A', r2.public_id, TRI, 3, '2026-08-29', { visibility: 'unlisted' }),
      study('RB-112', 'Diabetic nephropathy, class III', null, PAS, 2, '2026-08-26', { visibility: 'unlisted' }),
      study('RB-110', 'Focal segmental glomerulosclerosis, NOS variant', null, TRI, 3, '2026-08-24', { visibility: 'unlisted' }),
    ],
  },
  {
    public_id: 'col_000000000003', name: 'Dermpath teaching set', visibility: 'public', created_by: 'u2', author: 'Marta Kern',
    description: '<p>Inflammatory and neoplastic dermatopathology for residents.</p>',
    created_utc: '2026-08-01T10:00:00Z', modified_utc: '2026-08-31T10:00:00Z', like_count: 402, comment_count: 0, liked: true,
    groups: [],
    studies: [
      study('DP-231', 'Compound melanocytic nevus', null, HE, 2, '2026-08-31', { visibility: 'public' }),
      study('DP-228', 'Basal cell carcinoma, nodular', null, HE, 3, '2026-08-28', { visibility: 'public' }),
      study('DP-225', 'Psoriasis vulgaris', null, HE, 1, '2026-08-25', { visibility: 'public' }),
    ],
  },
  {
    public_id: 'col_000000000004', name: 'GI polyps: resident review', visibility: 'workspace', created_by: 'u1', author: 'Ana Alvarez',
    description: '<p>Adenomatous and serrated colorectal polyps.</p>', created_utc: '2026-08-01T10:00:00Z', modified_utc: '2026-08-23T10:00:00Z', like_count: 87, comment_count: 0, liked: false,
    groups: [],
    studies: [study('GI-044', 'Tubular adenoma, low grade', null, HE, 2, '2026-08-23'), study('GI-041', 'Sessile serrated lesion', null, HE, 3, '2026-08-21', { visibility: 'public', visibility_own: 'public' })],
  },
  {
    public_id: 'col_000000000005', name: 'Liver fibrosis staging', visibility: 'public', created_by: 'u2', author: 'Marta Kern',
    description: '<p>Trichrome-stained liver biopsies across METAVIR stages.</p>', created_utc: '2026-08-01T10:00:00Z', modified_utc: '2026-08-30T10:00:00Z', like_count: 53, comment_count: 0, liked: false,
    groups: [], studies: [study('LF-09', 'Bridging fibrosis, F3', null, TRI, 2, '2026-08-30', { visibility: 'public' }), study('LF-08', 'Cirrhosis, F4', null, TRI, 3, '2026-08-17', { visibility: 'public' })],
  },
  {
    public_id: 'col_000000000006', name: 'Fungal organisms', visibility: 'unlisted', created_by: 'u1', author: 'Ana Alvarez',
    description: '<p>GMS-highlighted fungal infections.</p>', created_utc: '2026-08-01T10:00:00Z', modified_utc: '2026-08-14T10:00:00Z', like_count: 31, comment_count: 0, liked: false,
    groups: [], studies: [study('FU-03', 'Aspergillus, invasive', null, GMS, 2, '2026-08-14', { visibility: 'unlisted' })],
  },
];

/**
 * The collections of the second workspace the account may write (ws_2): Copy
 * targets in another workspace, offered only to an administrator of both (give
 * ws_2 the role workspace_admin below to see them). Kept out of DATA, which is
 * what the collection list and Explore show for the default workspace.
 */
const OTHER_WORKSPACE: FxCollection[] = [
  {
    public_id: 'col_0000000000a1', name: 'Glioma grading', visibility: 'workspace', created_by: 'u1', author: 'Ana Alvarez',
    description: '<p>Diffuse gliomas across WHO grades 2 to 4.</p>', created_utc: '2026-08-10T10:00:00Z', modified_utc: '2026-08-12T10:00:00Z', like_count: 0, comment_count: 0, liked: false,
    groups: [group('Grade 4', 'Glioblastoma, IDH-wildtype.')], studies: [],
  },
];

const wider = (a: ServerVisibility, b: ServerVisibility): ServerVisibility => {
  const rank = { workspace: 0, org: 1, unlisted: 2, public: 3 };
  return rank[a] >= rank[b] ? a : b;
};
const recompute = (c: FxCollection) => c.studies.forEach((s) => (s.visibility = wider(s.visibility_own, c.visibility)));
DATA.forEach((c) => { c.studies.forEach((s) => { if (s.visibility !== 'workspace') s.visibility_own = s.visibility; }); recompute(c); });

const placementOrder = (c: FxCollection) => (a: FxStudy, b: FxStudy) => {
  const gi = (s: FxStudy) => (s.group_public_id ? c.groups.findIndex((g) => g.public_id === s.group_public_id) : 999);
  return gi(a) - gi(b) || Number(a.ordinal) - Number(b.ordinal);
};

function toListRow(c: FxCollection): Collection {
  const sorted = [...c.studies].sort(placementOrder(c));
  return {
    public_id: c.public_id, name: c.name, description: c.description, visibility: c.visibility, created_by: c.created_by, author: c.author,
    created_utc: c.created_utc, modified_utc: c.modified_utc, studies: c.studies.length, groups: c.groups.length,
    images: c.studies.reduce((a, s) => a + s.images, 0), covers: sorted.slice(0, 3).map((s) => s.public_id),
    like_count: c.like_count, comment_count: c.comment_count, liked: c.liked,
  };
}

function toDetail(c: FxCollection, editable: boolean): CollectionDetail {
  const sorted = [...c.studies].sort(placementOrder(c));
  return {
    publicId: c.public_id, name: c.name, description: c.description, visibility: c.visibility, author: c.author, created_by: c.created_by,
    created_utc: c.created_utc, modified_utc: c.modified_utc, like_count: c.like_count, comment_count: c.comment_count, liked: c.liked,
    groups: c.groups, studies: sorted.map(({ cover: _c, ...s }) => s), total: c.studies.length, limit: 500, offset: 0,
    editable,
  };
}

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), { status, headers: { 'content-type': 'application/json' } });
}
const none = () => new Response(null, { status: 204 });

/** Organ per fixture collection (the app derives it from the images' anatomic sites). */
const ORGANS: Record<string, string> = { col_000000000001: 'Breast', col_000000000002: 'Kidney', col_000000000003: 'Skin', col_000000000004: 'Colon', col_000000000005: 'Liver', col_000000000006: 'Lung' };

/** ?guest=1 makes the fake session answer 401 for the rest of the tab; ?guest=0 signs back in. */
const GUEST_KEY = 'tr.fixtures.guest';
function guestMode(): boolean {
  try {
    const q = new URLSearchParams(window.location.search).get('guest');
    if (q === '1') window.sessionStorage.setItem(GUEST_KEY, '1');
    if (q === '0') window.sessionStorage.removeItem(GUEST_KEY);
    return window.sessionStorage.getItem(GUEST_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * ?viewer=1 answers as somebody with the VIEWER role for the rest of the tab;
 * ?viewer=0 goes back to administering. Sticky like ?guest=1 and for the same
 * reason: what a viewer is offered is a property of the session, not of one
 * page, so it has to survive navigating between them.
 *
 * Worth having as a switch rather than a second fixture set — the pages read
 * one boolean (`editable`) and the whole difference between an editor's
 * screen and a viewer's is what that boolean removes, which is exactly the
 * thing to be able to look at.
 */
const VIEWER_KEY = 'tr.fixtures.viewer';
function viewerMode(): boolean {
  try {
    const q = new URLSearchParams(window.location.search).get('viewer');
    if (q === '1') window.sessionStorage.setItem(VIEWER_KEY, '1');
    if (q === '0') window.sessionStorage.removeItem(VIEWER_KEY);
    return window.sessionStorage.getItem(VIEWER_KEY) === '1';
  } catch {
    return false;
  }
}

function toCard(c: FxCollection, userId: string | null) {
  const row = toListRow(c);
  return { ...row, organ: ORGANS[c.public_id] ?? null, liked: userId ? c.liked : false, recent_likes: c.like_count % 17, own: userId !== null && c.created_by === userId };
}

const toPublicStudy = (s: FxStudy, userId: string | null) => ({ public_id: s.public_id, study_id: s.study_id, description: s.description, diagnosis: s.diagnosis, visibility: s.visibility, visibility_own: s.visibility_own, like_count: s.like_count, comment_count: s.comment_count, liked: userId ? s.liked : false, images: s.images, group_public_id: s.group_public_id, created_utc: s.created_utc });

export function installFixtures(): void {
  if (window.__TR_FIXTURES) return;
  window.__TR_FIXTURES = true;
  const guest = guestMode();
  // The server computes this per workspace; here one switch stands for "the
  // role I am signed in with", which is all the pages can tell apart anyway.
  const mayWrite = !viewerMode();
  const real = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = new URL(typeof input === 'string' ? input : input instanceof URL ? input.href : input.url, window.location.origin);
    // The server mounts everything but DICOMweb under /api (mount.ts); the
    // fixtures match the local paths, so the mount is stripped once here.
    const path = url.pathname.replace(/^\/api(?=\/|$)/, '');
    const method = (init?.method ?? 'GET').toUpperCase();
    const body = init?.body ? (JSON.parse(String(init.body)) as Record<string, unknown>) : {};
    const find = (id: string) => DATA.find((c) => c.public_id === id) ?? OTHER_WORKSPACE.find((c) => c.public_id === id);

    if (path === '/auth/session') return guest ? json({ error: 'unauthorized' }, 401) : json({ userId: 'u1', email: 'ana.alvarez@hospital.org', displayName: `${ME.givenName} ${ME.familyName}`, givenName: ME.givenName, familyName: ME.familyName, hasPassword: true, prefs: ME.prefs, superadmin: false, writeDenied: null });
    if (path === '/auth/profile' && method === 'GET') return json({ email: 'ana.alvarez@hospital.org', displayName: `${ME.givenName} ${ME.familyName}`, givenName: ME.givenName, familyName: ME.familyName, title: ME.title, institution: ME.institution, linkedin: ME.linkedin, roleTitle: ME.roleTitle, hasPassword: true, hasGoogle: false });
    if (path === '/auth/config') return json({ signup: true, edition: 'community', google: true });
    if (path === '/auth/profile' && method === 'PATCH') { Object.assign(ME, { givenName: body.givenName, familyName: body.familyName, title: body.title || null, institution: body.institution || null, linkedin: body.linkedin || null, roleTitle: body.roleTitle ?? ME.roleTitle }); return json({ displayName: `${ME.givenName} ${ME.familyName}` }); }
    if (path === '/auth/prefs') { Object.assign(ME.prefs, body); return json({ prefs: ME.prefs }); }
    if (path === '/auth/password') return String(body.current) === 'wrong' ? json({ error: 'that is not your current password' }, 401) : none();
    if (path === '/auth/account' && method === 'DELETE') return String(body.password) === 'wrong' ? json({ error: 'that is not your current password' }, 401) : none();
    if (path.startsWith('/auth/') && method === 'POST') {
      // the screens' flows: every step succeeds, a code of 000000 is refused
      if (path === '/auth/login' && String(body.password) === 'wrong') return json({ error: 'invalid email or password' }, 401);
      if ((path === '/auth/verify' || path === '/auth/reset/confirm') && String(body.code) === '000000') return json({ error: 'that code is not right, or it has expired' }, 400);
      if (path === '/auth/invitation') return json({ email: 'new.person@hospital.org', workspace: 'Pathology Dept.', role: 'member' });
      if (path === '/auth/signup' && body.invite) return json({ ok: true, invited: true, verified: true, workspace: 'ws_1' }, 201);
      return json({ ok: true }, path === '/auth/signup' ? 202 : 200);
    }
    // ?viewer=1 demotes the workspace this session is looking at, so the role
    // the Workspace page shows agrees with the controls the other pages offer.
    if (path === '/workspaces' && method === 'GET') {
      return json({ workspaces: WORKSPACES.filter((w) => !w.archived).map((w) => (mayWrite ? w : { ...w, role: 'viewer' })) });
    }
    if (path === '/workspaces' && method === 'POST') { const w = { public_id: `ws_${seq++}`, name: String(body.name), role: 'workspace_admin' as const, members: 1, is_default: false, archived: false, is_owner: true }; WORKSPACES.push(w); MEMBERS.set(w.public_id, { members: [{ ...PEOPLE[0], is_owner: true }], invitations: [] }); return json(w, 201); }
    const wm = /^\/workspaces\/(ws_[0-9a-z]+)(\/default|\/owner|\/members|\/members\/([^/]+)|\/invitations\/([^/]+))?$/.exec(path);
    if (wm) {
      const w = WORKSPACES.find((x) => x.public_id === wm[1]);
      if (!w) return json({ error: 'not found' }, 404);
      const mm = MEMBERS.get(w.public_id) ?? { members: [], invitations: [] };
      if (wm[2] === '/default') { WORKSPACES.forEach((x) => (x.is_default = x === w)); return none(); }
      if (!wm[2] && method === 'PATCH') { w.name = String(body.name); return json(w); }
      if (wm[2] === '/members' && method === 'GET') return json(mm);
      if (wm[2] === '/members' && method === 'POST') { mm.invitations.push({ public_id: `inv_${seq++}`, email: String(body.email), role: body.role as 'member', created_utc: new Date().toISOString(), expires_utc: new Date(Date.now() + 7 * 864e5).toISOString() }); return json({ invited: true, email: body.email, role: body.role }, 201); }
      if (wm[2] === '/owner' && method === 'POST') {
        const to = mm.members.find((x) => x.user_id === body.userId);
        if (!w.is_owner) return json({ error: 'only the owner of a workspace can hand it over' }, 403);
        if (!to || to.role !== 'workspace_admin') return json({ error: 'only an administrator of this workspace can own it. Make them one first.' }, 400);
        // Tom's storage is full, to show the refusal.
        if (to.user_id === 'u5') return json({ error: `${to.display_name} does not have enough free storage for this workspace. They can free up space, and you can try again.`, reason: 'storage-full' }, 409);
        mm.members.forEach((x) => (x.is_owner = x === to));
        w.is_owner = false;
        return json({ owner: to.user_id });
      }
      const owner = wm[3] ? mm.members.find((x) => x.user_id === wm[3] && x.is_owner) : undefined;
      if (wm[3] && method === 'PATCH') { if (owner && body.role !== 'workspace_admin') return json({ error: 'the owner of a workspace stays one of its administrators. Make someone else the owner first.' }, 400); const p = mm.members.find((x) => x.user_id === wm[3]); if (p) p.role = body.role as 'member'; return json(p ?? {}); }
      if (wm[3] && method === 'DELETE') { if (owner) return json({ error: 'the owner of a workspace cannot be removed from it.' }, 400); mm.members = mm.members.filter((x) => x.user_id !== wm[3]); w.members = mm.members.length; return none(); }
      if (wm[4] && method === 'DELETE') { mm.invitations = mm.invitations.filter((x) => x.public_id !== wm[4]); return none(); }
    }
    if (path === '/usage') {
      // The workspaces Ana owns: the Usage page counts those, whoever uploaded.
      const owned = WORKSPACES.filter((w) => w.is_owner);
      // The last collection's images stand in for the Teaching set's.
      const dataOf = (ws: string) => (ws === 'ws_1' ? DATA.slice(0, -1) : ws === 'ws_3' ? DATA.slice(-1) : []);
      const images = owned.flatMap((w) => dataOf(w.public_id).flatMap((c) => c.studies.flatMap((st) => Array.from({ length: st.images }, (_, i) => ({ image_id: imageId(st, i), study: st.public_id, series_instance_uid: `${st.study_instance_uid}.${i + 1}`, kind: i % 5 === 4 ? ('flat' as const) : ('wsi' as const), original_filename: `${st.study_id}-${['HE', 'ER', 'PR', 'HER2', 'Ki67', 'PAS'][i % 6]}.svs`, description: null, created_utc: st.created_utc, published: true, study_id: st.study_id, study_description: st.description, collection_id: c.public_id, collection_name: c.name, group_name: c.groups.find((g) => g.public_id === st.group_public_id)?.name ?? null, workspace_id: w.public_id, bytes: (((st.study_id ?? '').length * 131 + i * 977) % 1900 + 120) * 1024 ** 2 }))))).filter((im) => !DELETED.has(im.image_id));
      const of = (ws: string) => images.filter((im) => im.workspace_id === ws).reduce((a, r) => a + r.bytes, 0);
      const workspaces = owned.map((w) => ({ public_id: w.public_id, name: w.name, archived: false, usedBytes: of(w.public_id), pendingBytes: w.public_id === 'ws_1' ? 850 * 1024 ** 2 : 0 })).sort((a, b) => b.usedBytes - a.usedBytes);
      return json({ limitBytes: 250 * 1024 ** 3, usedBytes: images.reduce((a, r) => a + r.bytes, 0), pendingBytes: workspaces.reduce((a, w) => a + w.pendingBytes, 0), workspaces, images });
    }
    if (path === '/images/delete') { const ids = body.imageIds as string[]; ids.forEach((id) => DELETED.add(id)); return json({ deleted: ids.length, instances: ids.length, bytes: ids.length * 640 * 1024 ** 2 }); }

    if (path === '/collections/default' && method === 'POST') return json({ public_id: DATA[0]?.public_id ?? 'col_000000000001' });
    const toTarget = (c: FxCollection): CollectionTarget => ({ public_id: c.public_id, name: c.name, groups: c.groups.map((g) => ({ public_id: g.public_id, name: g.name })) });
    // The Move and Duplicate menus: every workspace the account may write (a
    // viewer's is left out), each with its own collections.
    if (path === '/collections/targets' && url.searchParams.get('scope') === 'writable') {
      return json({ workspaces: WORKSPACES.filter((w) => !w.archived && ['workspace_admin', 'member'].includes(w.role)).map((w) => ({ public_id: w.public_id, name: w.name, is_default: w.is_default, is_admin: w.role === 'workspace_admin', collections: (w.public_id === 'ws_1' ? DATA : w.public_id === 'ws_2' ? OTHER_WORKSPACE : []).map(toTarget) })) });
    }
    if (path === '/collections/targets') return json({ collections: DATA.map(toTarget) });
    if (path === '/collections' && method === 'GET') {
      const q = (url.searchParams.get('q') ?? '').toLowerCase();
      const tab = url.searchParams.get('tab');
      const vis = url.searchParams.get('visibility');
      const page = Number(url.searchParams.get('page') ?? 1);
      const limit = Number(url.searchParams.get('limit') ?? 12);
      let rows = DATA.filter((c) => (!q || c.name.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q)) && (tab !== 'saved' || c.liked) && (!vis || c.visibility === vis));
      const total = rows.length;
      const saved = rows.filter((c) => c.liked).length;
      rows = rows.slice((page - 1) * limit, page * limit);
      return json({ collections: rows.map(toListRow), total, saved, page, limit, editable: mayWrite });
    }
    if (path === '/collections' && method === 'POST') {
      if (DATA.some((c) => c.name === body.name)) return json({ error: 'a collection with that name already exists here' }, 409);
      const c: FxCollection = { public_id: `col_${(seq++).toString(16).padStart(12, '0')}`, name: String(body.name), description: null, visibility: 'workspace', created_by: 'u1', author: 'Ana Alvarez', created_utc: new Date().toISOString(), modified_utc: new Date().toISOString(), like_count: 0, comment_count: 0, liked: false, groups: [], studies: [] };
      DATA.unshift(c);
      return json(toListRow(c), 201);
    }
    let m = /^\/collections\/(col_[0-9a-f]{12})$/.exec(path);
    if (m) {
      const c = find(m[1]);
      if (!c) return json({ error: 'not found' }, 404);
      if (method === 'GET') return json(toDetail(c, mayWrite));
      if (method === 'PATCH') {
        if (typeof body.name === 'string') c.name = body.name;
        if (typeof body.description === 'string') c.description = body.description || null;
        if (typeof body.visibility === 'string') { c.visibility = body.visibility as ServerVisibility; recompute(c); }
        c.modified_utc = new Date().toISOString();
        return json(toListRow(c));
      }
      if (method === 'DELETE') { DATA.splice(DATA.indexOf(c), 1); return json({ deleted: true, studies: c.studies.length, jobs: [] }); }
    }
    m = /^\/collections\/(col_[0-9a-f]{12})\/groups$/.exec(path);
    if (m && method === 'POST') {
      const c = find(m[1])!;
      const g = group(String(body.name), typeof body.description === 'string' ? body.description : '');
      g.description = g.description || null;
      c.groups.push(g);
      return json(g, 201);
    }
    m = /^\/collections\/(col_[0-9a-f]{12})\/groups\/(grp_[0-9a-f]{12})(\/position|\/collection)?$/.exec(path);
    if (m) {
      const c = find(m[1])!;
      const g = c.groups.find((x) => x.public_id === m![2])!;
      if (m[3] === '/position') {
        const i = c.groups.indexOf(g);
        c.groups.splice(i, 1);
        const after = body.afterId ? c.groups.findIndex((x) => x.public_id === body.afterId) + 1 : c.groups.findIndex((x) => x.public_id === body.beforeId);
        c.groups.splice(after, 0, g);
        return json(g);
      }
      if (m[3] === '/collection') {
        const to = find(String(body.collectionId))!;
        c.groups.splice(c.groups.indexOf(g), 1);
        to.groups.push(g);
        const moving = c.studies.filter((s) => s.group_public_id === g.public_id);
        c.studies = c.studies.filter((s) => s.group_public_id !== g.public_id);
        to.studies.push(...moving);
        recompute(to);
        return json({ moved: moving.length });
      }
      if (method === 'PATCH') {
        if (typeof body.name === 'string') g.name = body.name;
        if (typeof body.description === 'string') g.description = body.description || null;
        return json(g);
      }
      if (method === 'DELETE') {
        c.groups.splice(c.groups.indexOf(g), 1);
        // ?studies=delete is the community page's destructive delete; without
        // it the studies drop to the ungrouped band. Same rule as the server.
        if (url.searchParams.get('studies') === 'delete') {
          const gone = c.studies.filter((s) => s.group_public_id === g.public_id);
          c.studies = c.studies.filter((s) => s.group_public_id !== g.public_id);
          recompute(c);
          return json({ deleted: true, ungrouped: 0, studies: gone.length, jobs: [] });
        }
        let n = 0;
        c.studies.forEach((s) => { if (s.group_public_id === g.public_id) { s.group_public_id = null; n++; } });
        return json({ deleted: true, ungrouped: n });
      }
    }
    m = /^\/likes\/(collection|study)\/(.+)$/.exec(path);
    if (m) {
      const id = decodeURIComponent(m[2]);
      const on = method === 'PUT';
      if (m[1] === 'collection') { const c = find(id)!; if (c.liked !== on) { c.liked = on; c.like_count += on ? 1 : -1; } }
      else for (const c of DATA) for (const s of c.studies) if (s.public_id === id && s.liked !== on) { s.liked = on; s.like_count += on ? 1 : -1; }
      return none();
    }
    // Copying a whole collection into another workspace: a new collection there,
    // its groups, and a copy of every study. The fixtures keep the copies in the
    // other workspace's list, where the page will not show them.
    m = /^\/collections\/([^/]+)\/copy$/.exec(path);
    if (m && method === 'POST') {
      const from = find(decodeURIComponent(m[1]));
      if (!from) return json({ error: 'not found' }, 404);
      const into: FxCollection = { ...from, public_id: `col_${seq++}`, groups: [], studies: [], visibility: 'workspace', like_count: 0, comment_count: 0, liked: false };
      for (const g of from.groups) into.groups.push(group(g.name, g.description ?? ''));
      for (const st of from.studies) into.studies.push({ ...st, public_id: `stu_${seq++}`, like_count: 0, comment_count: 0, liked: false, group_public_id: null });
      recompute(into);
      OTHER_WORKSPACE.push(into);
      return json({ collection: { public_id: into.public_id, name: into.name }, studies: from.studies.length, groups: from.groups.length, copying: from.studies.reduce((a, st) => a + st.images, 0) }, 201);
    }
    if (path === '/studies/duplicate' && method === 'POST') {
      const to = find(String(body.collectionId))!;
      const ids = (body.studyIds as string[] | undefined) ?? [];
      const gids = (body.groupIds as string[] | undefined) ?? [];
      let studies = 0;
      const copy = (src: FxStudy, groupId: string | null, same: boolean) => {
        const s2 = study(same && src.study_id ? `${src.study_id} (copy)` : (src.study_id ?? ''), src.description ?? '', groupId, src.cover, src.images, new Date().toISOString());
        s2.visibility_own = 'workspace';
        s2.like_count = 0;
        s2.comment_count = 0;
        to.studies.push(s2);
        studies++;
      };
      for (const c of DATA) for (const st of c.studies) if (ids.includes(st.public_id)) copy(st, (body.studyGroupId as string) || null, c === to);
      for (const c of DATA) for (const g of c.groups) if (gids.includes(g.public_id)) {
        const ng = group(c === to ? `${g.name} (copy)` : g.name, g.description ?? '');
        to.groups.push(ng);
        for (const st of c.studies) if (st.group_public_id === g.public_id) copy(st, ng.public_id, c === to);
      }
      recompute(to);
      // Into the other workspace the images are copied in the background; the
      // fixtures have no background, so they are simply counted.
      const copying = OTHER_WORKSPACE.includes(to) && studies ? to.studies.slice(-studies).reduce((a, st) => a + st.images, 0) : 0;
      return json({ studies, groups: gids.length, copying });
    }
    if (path === '/studies/move' && method === 'POST') {
      const ids = body.studyIds as string[];
      const to = find(String(body.collectionId))!;
      // Slides do not move between workspaces; the server refuses it.
      if (OTHER_WORKSPACE.includes(to)) return json({ error: 'slides do not move between workspaces; copy them there instead' }, 400);
      let moved = 0;
      for (const c of DATA) {
        const mv = c.studies.filter((s) => ids.includes(s.public_id));
        c.studies = c.studies.filter((s) => !ids.includes(s.public_id));
        mv.forEach((s) => { s.group_public_id = (body.studyGroupId as string) || null; to.studies.push(s); moved++; });
      }
      recompute(to);
      return json({ moved });
    }
    m = /^\/studies\/([^/]+)$/.exec(path);
    // Writes only: a GET is the viewer's header, answered further down.
    if (m && method !== 'GET') {
      const id = decodeURIComponent(m[1]);
      for (const c of DATA) {
        const s = c.studies.find((x) => x.public_id === id);
        if (!s) continue;
        if (method === 'DELETE') { c.studies.splice(c.studies.indexOf(s), 1); return json({ deleted: true, studies: 1, jobs: [] }); }
        if (method === 'PATCH') { s.visibility_own = body.visibility as ServerVisibility; recompute(c); return json({ public_id: id, visibility: s.visibility, visibility_own: s.visibility_own }); }
      }
      return json({ error: 'not found' }, 404);
    }
    // ── explore, public pages, search, home ──
    const me = guest ? null : 'u1';
    const isPublic = path.startsWith('/public/');
    const who = isPublic ? null : me;
    if (path === '/explore' || path === '/public/explore') {
      const q = (url.searchParams.get('q') ?? '').toLowerCase();
      const organ = url.searchParams.get('organ') ?? 'All';
      const sort = url.searchParams.get('sort') ?? 'trending';
      const all = DATA.filter((c) => c.visibility === 'public').map((c) => toCard(c, who));
      const organs = [...new Set(all.map((c) => c.organ).filter((o): o is string => !!o))].sort();
      const collections = all
        .filter((c) => (organ === 'All' || c.organ === organ) && (!q || [c.name, c.description ?? '', c.author ?? '', c.organ ?? ''].some((t) => t.toLowerCase().includes(q))))
        .sort((a, b) => (sort === 'recent' ? b.created_utc.localeCompare(a.created_utc) : sort === 'saves' ? b.like_count - a.like_count : b.recent_likes - a.recent_likes || b.like_count - a.like_count));
      const open = (c: FxCollection) => c.visibility === 'public' || c.visibility === 'unlisted';
      const studies = DATA.flatMap((c) => c.studies.filter((st) => st.visibility === 'public').map((st) => ({ public_id: st.public_id, study_id: st.study_id, description: st.description, images: st.images, like_count: st.like_count, comment_count: st.comment_count, liked: who ? st.liked : false, collection_id: open(c) ? c.public_id : null, collection_name: open(c) ? c.name : null, author: c.author, created_utc: st.created_utc })))
        .filter((st) => !q || [st.study_id ?? '', st.description ?? '', st.collection_name ?? ''].some((t) => t.toLowerCase().includes(q)));
      return json({ collections, organs, studies });
    }
    m = /^\/(?:public|explore)\/collections\/(col_[0-9a-f]{12})$/.exec(path);
    if (m) {
      const c = find(m[1]);
      if (!c || (c.visibility !== 'public' && c.visibility !== 'unlisted')) return json({ error: 'not found' }, 404);
      const sorted = [...c.studies].sort(placementOrder(c));
      return json({ ...toCard(c, who), groups: c.groups, studies: sorted.map((st) => toPublicStudy(st, who)) });
    }
    m = /^\/public\/comments\/(collection|study)\/([^/]+)$/.exec(path);
    if (m) {
      const list = COMMENTS.get(`${m[1]}:${decodeURIComponent(m[2])}`) ?? [];
      const strip = (x: FxComment): FxComment => ({ ...x, authorId: '', isYou: false, replies: x.replies.map(strip) });
      return json({ comments: list.map(strip) });
    }
    // The signed-in study header (the viewer's top bar): editable, placed, and
    // the workspace its DICOMweb is read under.
    m = /^\/studies\/(stu_[0-9a-f]{12})$/.exec(path);
    if (m && method === 'GET') {
      if (guest) return json({ error: 'not found' }, 404);
      const id = m[1];
      for (const c of DATA) {
        const st = c.studies.find((x) => x.public_id === id);
        if (!st) continue;
        const g = c.groups.find((x) => x.public_id === st.group_public_id);
        return json({ ...toPublicStudy(st, 'u1'), study_instance_uid: st.study_instance_uid, sex: st.sex ?? null, age: st.age ?? null, study_date: st.study_date ?? null, unpublished: st.unpublished, collection_id: c.public_id, collection_name: c.name, group_id: g?.public_id ?? null, group_name: g?.name ?? null, collection_visibility: c.visibility, workspace: 'ws_1', workspace_id: 'ws_1', editable: mayWrite });
      }
      return json({ error: 'not found' }, 404);
    }
    // The top bar's list: nothing from another tab or device, in the fake world.
    if (path === '/uploads/active' && method === 'GET') {
      if (guest) return json({ error: 'unauthorized' }, 401);
      return json({ studies: [] });
    }
    if (/^\/uploads\/active\/stu_[0-9a-f]{12}$/.test(path) && method === 'DELETE') return new Response(null, { status: 204 });
    if (path === '/search') {
      if (guest) return json({ error: 'unauthorized' }, 401);
      const q = (url.searchParams.get('q') ?? '').trim().toLowerCase();
      const hit = (...t: Array<string | null | undefined>) => !!q && t.some((x) => (x ?? '').toLowerCase().includes(q));
      const sec = <T,>(items: T[]) => ({ items: items.slice(0, 5), total: items.length });
      const cols = DATA.filter((c) => hit(c.name, c.description)).map((c) => ({ public_id: c.public_id, name: c.name, description: c.description, studies: c.studies.length, cover: [...c.studies].sort(placementOrder(c))[0]?.public_id ?? null }));
      const groups = DATA.flatMap((c) => c.groups.filter((g) => hit(g.name, g.description)).map((g) => ({ public_id: g.public_id, name: g.name, collection_id: c.public_id, collection_name: c.name, position: c.groups.indexOf(g) + 1, studies: c.studies.filter((st) => st.group_public_id === g.public_id).length })));
      const studies = DATA.flatMap((c) => c.studies.filter((st) => hit(st.study_id, st.description, st.diagnosis)).map((st) => ({ public_id: st.public_id, study_id: st.study_id, description: st.description, collection_id: c.public_id, collection_name: c.name, group_name: c.groups.find((g) => g.public_id === st.group_public_id)?.name ?? null, images: st.images })));
      const images = DATA.flatMap((c) => c.studies.flatMap((st) => Array.from({ length: st.images }, (_, i) => ({ image_id: imageId(st, i), study: st.public_id, series_instance_uid: `${st.study_instance_uid}.${i + 1}`, original_filename: `${st.study_id}-${['HE', 'ER', 'PR', 'HER2', 'Ki67', 'PAS'][i % 6]}.svs`, description: null, stain_meaning: ['H&E', 'ER', 'PR', 'HER2', 'Ki-67', 'PAS'][i % 6], kind: i % 5 === 4 ? ('flat' as const) : ('wsi' as const), study_id: st.study_id, collection_id: c.public_id, collection_name: c.name })).filter((im) => hit(im.original_filename, im.stain_meaning))));
      return json({ collections: sec(cols), groups: sec(groups), studies: sec(studies), images: sec(images) });
    }
    if (path === '/home') {
      if (guest) return json({ error: 'unauthorized' }, 401);
      const recent = [...DATA].sort((a, b) => b.modified_utc.localeCompare(a.modified_utc));
      const allStudies = DATA.flatMap((c) => c.studies.map((st) => ({ c, st }))).sort((a, b) => b.st.created_utc.localeCompare(a.st.created_utc));
      return json({
        workspace: 'Pathology Dept.',
        collectionCount: DATA.length,
        recentCollections: recent.slice(0, 4).map((c) => toListRow(c)),
        recentStudies: allStudies.slice(0, 6).map(({ c, st }) => ({ public_id: st.public_id, study_id: st.study_id, description: st.description, visibility: st.visibility, visibility_own: st.visibility_own, like_count: st.like_count, liked: st.liked, images: st.images, parent: c.groups.find((g) => g.public_id === st.group_public_id)?.name ?? c.name, created_utc: st.created_utc })),
        recentUploads: allStudies.slice(0, 6).map(({ st }, i) => ({ image_id: imageId(st, 0), study: st.public_id, series_instance_uid: `${st.study_instance_uid}.1`, original_filename: `${st.study_id}-${['HE', 'ER', 'PR', 'HER2', 'Ki67', 'PAS'][i % 6]}.svs`, description: null, kind: i % 5 === 4 ? 'flat' : 'wsi', image_type: null, study_id: st.study_id, published: true })),
      });
    }
    if (guest && (path === '/workspaces' || path === '/notifications' || path.startsWith('/collections') || path.startsWith('/comments/') || path.startsWith('/likes/'))) return json({ error: 'unauthorized' }, 401);

    // ── discussion ──
    m = /^\/comments\/(collection|study)\/([^/]+)(?:\/([^/]+))?$/.exec(path);
    if (m) {
      const key = `${m[1]}:${decodeURIComponent(m[2])}`;
      const list = COMMENTS.get(key) ?? [];
      if (method === 'GET') return json({ comments: list });
      if (method === 'POST') {
        const row = { id: `c${seq++}`, authorId: 'u1', name: 'Ana Alvarez', isYou: true, body: String(body.body), deleted: false, createdUtc: new Date().toISOString(), editedUtc: null, replies: [] as FxComment[] };
        if (body.parentId) list.find((c) => c.id === body.parentId)?.replies.push(row);
        else list.push(row);
        COMMENTS.set(key, list);
        return json(row, 201);
      }
      const cid = m[3] ? decodeURIComponent(m[3]) : '';
      for (const c of list) {
        const hit = c.id === cid ? c : c.replies.find((r) => r.id === cid);
        if (!hit) continue;
        if (method === 'PATCH') { hit.body = String(body.body); hit.editedUtc = new Date().toISOString(); return json({ id: cid, body: hit.body, editedUtc: hit.editedUtc }); }
        if (method === 'DELETE') { if (hit === c) list.splice(list.indexOf(c), 1); else c.replies.splice(c.replies.indexOf(hit), 1); return none(); }
      }
      return json({ error: 'not found' }, 404);
    }
    if (path === '/notifications' && method === 'GET') return json({ unread: NOTIFS.filter((n) => !n.read).length, notifications: NOTIFS });
    if (path === '/notifications/read') { NOTIFS.forEach((n) => { if (body.all || (body.ids as string[])?.includes(n.id)) n.read = true; }); return none(); }

    // ── drafts: the study and its images before their bytes (server/src/http/drafts.ts) ──
    const phi = (...t: unknown[]) => t.some((x) => typeof x === 'string' && /\b\d{6,}\b/.test(x));
    const refusePhi = (fields: Record<string, unknown>) => json({ error: 'a description looks like it contains an identifier or a date', fields: Object.entries(fields).filter(([, v]) => phi(v)).map(([field, v]) => ({ field, fragment: /\d{6,}/.exec(String(v))?.[0] ?? '', why: 'a number that looks like a record number' })) }, 422);
    const studyOf = (id: string) => { for (const c of DATA) { const st = c.studies.find((x) => x.public_id === id); if (st) return st; } return undefined; };
    if (path === '/studies' && method === 'POST') {
      if (phi(body.description, body.diagnosis)) return refusePhi({ description: body.description, diagnosis: body.diagnosis });
      const c = find(String(body.collectionId));
      if (!c) return json({ error: 'not found' }, 404);
      const st = study(String(body.study_id), typeof body.description === 'string' ? body.description : '', (body.studyGroupId as string) || null, HE, 0, new Date().toISOString());
      st.diagnosis = typeof body.diagnosis === 'string' ? body.diagnosis : null;
      c.studies.push(st);
      recompute(c);
      return json({ public_id: st.public_id, study_instance_uid: st.study_instance_uid, workspace: 'ws_1' }, 201);
    }
    m = /^\/studies\/(stu_[0-9a-f]{12})\/details$/.exec(path);
    if (m && method === 'PATCH') {
      const st = studyOf(m[1]);
      if (!st) return json({ error: 'not found' }, 404);
      if (phi(body.description, body.diagnosis)) return refusePhi({ description: body.description, diagnosis: body.diagnosis });
      if ('study_id' in body && !String(body.study_id ?? '').trim()) return json({ error: 'study_id cannot be empty' }, 400);
      if ('study_id' in body) st.study_id = String(body.study_id);
      if ('description' in body) st.description = (body.description as string | null) ?? null;
      if ('diagnosis' in body) st.diagnosis = (body.diagnosis as string | null) ?? null;
      if ('sex' in body) st.sex = (body.sex as FxStudy['sex']) ?? null;
      // The server's AS form: years, anyone 90 or over as 090Y.
      if ('age' in body) st.age = body.age === null ? null : `${String(Math.min(90, parseInt(String(body.age), 10) || 0)).padStart(3, '0')}Y`;
      if ('study_datetime' in body) {
        const d = body.study_datetime;
        if (d !== null && !(typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d))) return json({ error: 'invalid study_datetime' }, 400);
        st.study_date = d as string | null;
      }
      return json({ public_id: st.public_id, study_id: st.study_id, description: st.description, diagnosis: st.diagnosis, sex: st.sex ?? null, age: st.age ?? null, study_date: st.study_date ?? null });
    }
    m = /^\/studies\/(stu_[0-9a-f]{12})\/images$/.exec(path);
    if (m && method === 'POST') {
      const st = studyOf(m[1]);
      if (!st) return json({ error: 'not found' }, 404);
      // The page says what it takes the file for (`kind`) when its name is certain, and the Type is checked against that.
      const d: FxDraft = { image_id: `img_${hex12(seq++)}`, study: st.public_id, name: String(body.name), series_instance_uid: uid(), kind: body.kind === 'flat' || body.kind === 'wsi' ? body.kind : undefined, created: Date.now(), uploadId: null, size: null, arrivedAt: null, fail: /fail/i.test(String(body.name)) };
      const refused = refuseDetails(d, body);
      if (refused) return refused;
      applyDetails(d, body);
      DRAFTS.set(d.image_id, d);
      COVERS[d.image_id] = st.cover;
      st.images++;
      st.unpublished++;
      return json(draftView(d), 201);
    }
    if (m && method === 'GET') {
      const st = studyOf(m[1]);
      if (!st) return json({ error: 'not found' }, 404);
      seedPublished(st);
      return json({ images: [...DRAFTS.values()].filter((d) => d.study === m![1]).map(draftView) });
    }
    m = /^\/images\/(img_[0-9a-f]{12})$/.exec(path);
    if (m) {
      const d = DRAFTS.get(m[1]);
      if (!d) return json({ error: 'not found' }, 404);
      const v = draftView(d);
      if (method === 'PATCH') {
        if (phi(body.description)) return refusePhi({ description: body.description });
        if (v.stage === 'published' && ((('image_type' in body) && !body.image_type) || (('site' in body) && !body.site))) return json({ error: 'a published image keeps its type and anatomic site' }, 400);
        const refused = refuseDetails(d, body);
        if (refused) return refused;
        applyDetails(d, body);
        return json(draftView(d));
      }
      if (method === 'DELETE') {
        if (v.stage === 'published') return json({ error: 'a published image is deleted from its study, not withdrawn' }, 409);
        DRAFTS.delete(d.image_id);
        const st = studyOf(d.study);
        if (st) { st.images--; st.unpublished--; }
        return none();
      }
    }
    // Placing the study's images (drafts.ts PUT /studies/:id/order): every published image takes its place in the order sent, numbered within its group; one not sent goes after.
    m = /^\/studies\/(stu_[0-9a-f]{12})\/order$/.exec(path);
    if (m && method === 'PUT') {
      const studyId = m[1];
      if (!studyOf(studyId)) return json({ error: 'not found' }, 404);
      const sent: unknown[] = Array.isArray(body.images) ? body.images : [];
      if (!sent.length) return json({ error: 'images: the image ids, in order' }, 400);
      // Unsent drafts rank after every sent one, and among themselves by their order so far.
      const rank = (d: FxDraft) => { const at = sent.indexOf(d.image_id); return at < 0 ? sent.length : at; };
      const bands = new Map<string, FxDraft[]>();
      for (const d of DRAFTS.values()) {
        if (d.study !== studyId || draftView(d).stage !== 'published') continue;
        const key = bandOf(d);
        bands.set(key, [...(bands.get(key) ?? []), d]);
      }
      for (const band of bands.values()) band.sort((x, y) => rank(x) - rank(y) || byOrder(x, y)).forEach((x, i) => (x.position = i + 1));
      return none();
    }
    // Seen marks (seen.ts): the first look is kept; a reset forgets the study's.
    m = /^\/images\/(img_[0-9a-f]{12})\/seen$/.exec(path);
    if (m && method === 'PUT') {
      if (guest) return json({ error: 'sign in to keep track of what you have seen' }, 401);
      const d = DRAFTS.get(m[1]);
      if (!d || draftView(d).stage !== 'published') return json({ error: 'not found' }, 404);
      const marks = SEEN.get(d.study) ?? new Map<string, string>();
      if (!marks.has(d.image_id)) marks.set(d.image_id, new Date().toISOString());
      SEEN.set(d.study, marks);
      return none();
    }
    m = /^\/studies\/(stu_[0-9a-f]{12})\/seen$/.exec(path);
    if (m && method === 'DELETE') {
      if (guest) return json({ error: 'sign in to keep track of what you have seen' }, 401);
      SEEN.delete(m[1]);
      return none();
    }
    // The navigator (collections.ts GET /studies/:id/navigator): the published images in their order, and whether anybody placed them (else the page shows each group by name).
    m = /^\/studies\/(stu_[0-9a-f]{12})\/navigator$/.exec(path);
    if (m && method === 'GET') {
      const st = studyOf(m[1]);
      if (!st) return json({ error: 'not found' }, 404);
      seedPublished(st);
      const rows = [...DRAFTS.values()].filter((d) => d.study === m![1]).map((d) => ({ d, v: draftView(d) })).filter((x) => x.v.stage === 'published');
      const code = (c: { code: string; meaning: string } | null | undefined) => (c ? { code: c.code, meaning: c.meaning } : null);
      // What this reader has seen (seen.ts): a guest's marks are not kept.
      const seen = SEEN.get(m![1]);
      const images = rows.sort((a, b) => byOrder(a.d, b.d)).map(({ d, v }) => ({
        image_id: d.image_id, series_instance_uid: d.series_instance_uid, kind: v.kind, type: v.image_type, name: d.description ?? d.name,
        stain: code(d.stain), site: code(d.site), laterality: code(d.laterality), specimen_id: null,
        ...(guest ? {} : { seen_utc: seen?.get(d.image_id) ?? null }),
      }));
      return json({ editable: mayWrite, seen_tracked: !guest, placed: rows.some(({ d }) => d.position != null), images });
    }

    // ── the upload transport (bytes never leave the tab) ──
    m = /^\/uploads\/images\/(img_[0-9a-f]{12})$/.exec(path);
    if (m && method === 'POST') {
      const d = DRAFTS.get(m[1]);
      if (!d) return json({ error: 'not found' }, 404);
      if (d.arrivedAt) return json({ error: 'this image already has its file', reason: 'has-file' }, 409);
      const id = `up_${(seq++).toString(36)}`;
      const size = Number(body.size ?? 0);
      const partSize = 5 * 1024 * 1024;
      const partCount = Math.max(1, Math.ceil(size / partSize));
      Object.assign(d, { uploadId: id, size });
      UPLOADS.set(id, d.image_id);
      const urls = Array.from({ length: Math.min(partCount, 8) }, (_, i) => ({ partNumber: i + 1, url: `/uploads/${id}/parts/${i + 1}` }));
      return json({ uploadId: id, name: d.name, partSize, partCount, partConcurrency: 3, urlTtlSeconds: 900, heartbeatMs: 45_000, urls }, 201);
    }
    if (path === '/uploads/parts-urls' && method === 'POST') {
      return json({ urls: (body.partNumbers as number[]).map((n) => ({ partNumber: n, url: `/uploads/${body.uploadId}/parts/${n}` })) });
    }
    // Resume after a reload: the fake keeps no parts, so the upload starts over.
    if (/^\/uploads\/[^/]+\/parts$/.test(path)) return json({ error: 'unknown upload' }, 404);
    m = /^\/uploads\/([^/]+)\/(complete|abort|touch)$/.exec(path);
    if (m) {
      const d = DRAFTS.get(UPLOADS.get(m[1]) ?? '');
      if (d && m[2] === 'complete') d.arrivedAt = Date.now();
      if (d && m[2] === 'abort' && !d.arrivedAt) d.uploadId = null;
      if (m[2] !== 'touch') UPLOADS.delete(m[1]);
      return json({ ok: true });
    }
    return real(input, init);
  };

  // Part PUTs go over XHR (progress events); shim them for /uploads/<id>/parts/<n>.
  const XHR = XMLHttpRequest.prototype;
  const realOpen = XHR.open;
  const realSend = XHR.send;
  XHR.open = function (this: XMLHttpRequest & { __fx?: { id: string; n: number } }, method: string, u: string | URL, ...rest: unknown[]) {
    const mm = /\/uploads\/([^/]+)\/parts\/(\d+)$/.exec(String(u));
    if (mm) this.__fx = { id: mm[1], n: Number(mm[2]) };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (realOpen as any).call(this, method, u, ...rest);
  };
  XHR.send = function (this: XMLHttpRequest & { __fx?: { id: string; n: number } }, bodyArg?: Document | XMLHttpRequestBodyInit | null) {
    const fx = this.__fx;
    if (!fx) return realSend.call(this, bodyArg);
    const total = bodyArg instanceof Blob ? bodyArg.size : 1;
    const failAt = DRAFTS.get(UPLOADS.get(fx.id) ?? '')?.fail && fx.n === 1 ? 0.38 : null;
    let done = 0;
    const steps = 6;
    let i = 0;
    const tick = () => {
      i++;
      done = Math.min(total, Math.round((total * i) / steps));
      this.upload.dispatchEvent(new ProgressEvent('progress', { lengthComputable: true, loaded: done, total }));
      if (failAt !== null && i / steps >= failAt) {
        Object.defineProperty(this, 'status', { value: 0, configurable: true });
        this.dispatchEvent(new Event('error'));
        return;
      }
      if (i < steps) { setTimeout(tick, 120); return; }
      Object.defineProperty(this, 'status', { value: 200, configurable: true });
      Object.defineProperty(this, 'responseText', { value: JSON.stringify({ etag: `etag-${fx.n}` }), configurable: true });
      Object.defineProperty(this, 'getResponseHeader', { value: () => null, configurable: true });
      this.dispatchEvent(new Event('load'));
    };
    setTimeout(tick, 120);
  };
}

interface FxComment { id: string; authorId: string; name: string; isYou: boolean; body: string; deleted: boolean; createdUtc: string; editedUtc: string | null; replies: FxComment[] }
const ago = (h: number) => new Date(Date.now() - h * 3600e3).toISOString();
const COMMENTS = new Map<string, FxComment[]>([
  ['collection:col_000000000001', [
    { id: 'c1', authorId: 'u2', name: 'Marta Kern', isYou: false, body: 'FISH results are back for the HER2 equivocal group. I\'ll update each description this week.', deleted: false, createdUtc: ago(48), editedUtc: null, replies: [
      { id: 'c2', authorId: 'u1', name: 'Ana Alvarez', isYou: true, body: 'Thanks Marta. Ping me when they are in and I\'ll re-review the group.', deleted: false, createdUtc: ago(24), editedUtc: null, replies: [] },
    ] },
    { id: 'c3', authorId: 'u1', name: 'Ana Alvarez', isYou: true, body: 'Reminder for everyone: score blind first, then open the consensus read.', deleted: false, createdUtc: ago(72), editedUtc: null, replies: [
      { id: 'c4', authorId: 'u4', name: 'Priya Natarajan', isYou: false, body: 'Did exactly that with the residents yesterday, worked well.', deleted: false, createdUtc: ago(48), editedUtc: null, replies: [] },
      { id: 'c5', authorId: 'u3', name: 'Jonas Ruiz', isYou: false, body: 'Could we add a printable scoring sheet to the description?', deleted: false, createdUtc: ago(24), editedUtc: ago(20), replies: [] },
    ] },
  ]],
]);
const NOTIFS = [
  { id: 'n1', kind: 'reply', actor: 'Jonas Ruiz', targetKind: 'collection', targetId: 'col_000000000001', collectionId: 'col_000000000001', title: 'Breast IHC panel', preview: 'Could we add a printable scoring sheet to the description?', createdUtc: ago(24), read: false },
  { id: 'n2', kind: 'comment', actor: 'Marta Kern', targetKind: 'collection', targetId: 'col_000000000001', collectionId: 'col_000000000001', title: 'Breast IHC panel', preview: 'FISH results are back for the HER2 equivocal group.', createdUtc: ago(48), read: false },
  { id: 'n3', kind: 'comment', actor: 'Tom Adeyemi', targetKind: 'collection', targetId: 'col_000000000002', collectionId: 'col_000000000002', title: 'Renal biopsies 2026', preview: 'RB-118: is the degree of tubular atrophy consistent with stage II? Feels like more.', createdUtc: ago(1), read: true },
];

/** Draft images by id, and live uploads (upload id → image id). A name with "fail" in it fails. */
interface FxDraft {
  image_id: string;
  study: string;
  name: string;
  series_instance_uid: string;
  /** What the page said the file was at its create; the name's word otherwise. */
  kind?: ImageKind;
  created: number;
  uploadId: string | null;
  size: number | null;
  arrivedAt: number | null;
  fail: boolean;
  /** The Type chosen; the modality follows it. Null is "Other" (a modality none of the seven fits). */
  image_type?: ImageType | null;
  modality?: string | null;
  description?: string | null;
  site?: { code: string; scheme: string; meaning: string } | null;
  /** CID 244, stored as the server does: the code, whatever the site. */
  laterality?: { code: string; scheme: string; meaning: string } | null;
  stain?: { code: string; scheme: string; meaning: string } | null;
  objective_power?: number | null;
  /** Its place in the navigator's order; none until the study is placed (shown by name then). */
  position?: number;
  /** When it was shown to everyone; the study's unpublished count drops then. */
  publishedAt?: number;
}
const DRAFTS = new Map<string, FxDraft>();
/** Seen marks by study, then image: when the reader first saw it (seen.ts). */
const SEEN = new Map<string, Map<string, string>>();
const UPLOADS = new Map<string, string>();

/** A slide's pyramid or a flat picture: what the page said, else the name's word (the converter's, here). */
const kindOfDraft = (d: FxDraft): ImageKind => d.kind ?? (/\.(png|jpe?g)$/i.test(d.name) ? 'flat' : 'wsi');
/** The converter has spoken: the kind is known, and the Type is held to it. */
/** The Type a modality stands for when nobody chose (0088's image_type_of). */
function derivedType(modality: string | null | undefined, kind: ImageKind): ImageType | null {
  switch (modality) {
    case 'SM':
      return kind === 'wsi' ? 'wsi' : 'photomicrograph';
    case 'GM':
      return 'photomicrograph';
    case 'XC':
      return 'gross';
    case 'DX':
    case 'CR':
    case 'RG':
      return 'radiography';
    case 'DOC':
      return 'document';
    default:
      return null;
  }
}
/** The navigator's order: placed positions first, then creation. */
const byOrder = (a: FxDraft, b: FxDraft) => (a.position ?? Infinity) - (b.position ?? Infinity) || a.created - b.created || a.image_id.localeCompare(b.image_id);
/** The group an image is placed within: the same Type, site and laterality (community has no specimen id). */
const bandOf = (d: FxDraft) => [draftView(d).image_type ?? '', d.site?.code ?? '', d.laterality?.code ?? ''].join('|');

/**
 * A seeded study's published images as drafts the editor can list and change,
 * made the first time they are asked for: the site of the study's collection
 * (Breast for the panel, Kidney for the renal set…), a side for a paired
 * organ, the stains in turn, and the objective power ingest would have read
 * from the file. Every fifth image is a flat picture, typed as gross.
 */
const SEEDED = new Set<string>();
const PAIRED = new Set(['Breast', 'Kidney', 'Lung']);
const FX_SITES: Record<string, { code: string; meaning: string }> = {
  Breast: { code: '76752008', meaning: 'Breast' }, Kidney: { code: '64033007', meaning: 'Kidney' }, Skin: { code: '39937001', meaning: 'Skin' },
  Colon: { code: '71854001', meaning: 'Colon' }, Liver: { code: '10200004', meaning: 'Liver' }, Lung: { code: '39607008', meaning: 'Lung' },
};
function seedPublished(st: FxStudy): void {
  if (SEEDED.has(st.public_id)) return;
  SEEDED.add(st.public_id);
  const c = DATA.find((x) => x.studies.includes(st));
  const site = FX_SITES[ORGANS[c?.public_id ?? ''] ?? ''] ?? null;
  const have = [...DRAFTS.values()].filter((d) => d.study === st.public_id).length;
  for (let i = 0; i < st.images - have; i++) {
    const id = imageId(st, i);
    const stain = i % 3 === 2 ? { code: 'TRST578', scheme: STAIN_SCHEME, meaning: 'Trichrome' } : { code: 'TRST001', scheme: STAIN_SCHEME, meaning: 'H&E' };
    const flat = i % 5 === 4;
    DRAFTS.set(id, {
      image_id: id, study: st.public_id, name: `${st.study_id}-${['HE', 'ER', 'PR', 'HER2', 'Ki67', 'PAS'][i % 6]}.${flat ? 'jpg' : 'svs'}`, series_instance_uid: `${st.study_instance_uid}.${i + 1}`, kind: flat ? 'flat' : 'wsi',
      created: Date.parse(st.created_utc) + i * 60e3, uploadId: null, size: 640 * 1024 ** 2, arrivedAt: Date.parse(st.created_utc), fail: false, publishedAt: Date.parse(st.created_utc),
      image_type: flat ? 'gross' : 'wsi', modality: flat ? 'XC' : 'SM', description: i === 0 ? 'Diagnostic block' : null, site: site ? { ...site, scheme: 'SCT' } : null,
      laterality: site && PAIRED.has(site.meaning) ? { ...LATERALITY_CODES[i % 2 ? 'L' : 'R'] } : null, stain, objective_power: flat ? null : i % 2 ? 20 : 40,
    });
  }
}

/**
 * What the server refuses (drafts.ts imageColumns): a laterality that is not
 * one of the three, a Type that is none of the seven, and a flat-picture Type
 * for a file that is certainly a slide (a scanner's format). Any other
 * picture may be any Type, however it is stored.
 */
function refuseDetails(d: FxDraft, body: Record<string, unknown>): Response | null {
  if ('image_type' in body && body.image_type !== null && !(isImageType(body.image_type) && typeFits(body.image_type, certainKind(d.name)))) return json({ error: 'invalid image_type' }, 400);
  if ('laterality' in body && body.laterality !== null && !isLaterality(body.laterality)) return json({ error: 'invalid laterality' }, 400);
  return null;
}

function applyDetails(d: FxDraft, body: Record<string, unknown>): void {
  for (const k of ['modality', 'description', 'site', 'stain', 'objective_power'] as const) if (k in body) (d as unknown as Record<string, unknown>)[k] = body[k] ?? null;
  // The Type writes the modality; a laterality is kept as its CID 244 code.
  if ('image_type' in body) {
    d.image_type = isImageType(body.image_type) ? body.image_type : null;
    if (d.image_type) d.modality = modalityOf(d.image_type, kindOfDraft(d));
  }
  if ('laterality' in body) d.laterality = isLaterality(body.laterality) ? { ...LATERALITY_CODES[body.laterality] } : null;
}

/**
 * Where a draft is, by the clock since its bytes arrived — the server's
 * figures (server/src/catalog/progress.ts): conversion 8 to 85, indexing 90.
 * Indexing fills an empty modality (SM) the way ingest does; an image with its
 * site then publishes by itself. A Type nobody chose is derived from the
 * modality, as 0088's trigger does.
 */
function draftView(d: FxDraft) {
  const kind = kindOfDraft(d);
  const t = d.arrivedAt ? (Date.now() - d.arrivedAt) / 1000 : -1;
  let stage: 'waiting-file' | 'uploading' | 'converting' | 'ingesting' | 'needs-details' | 'published' | 'failed';
  let percent: number | undefined;
  let error: string | null = null;
  if (t < 0) stage = d.uploadId ? 'uploading' : 'waiting-file';
  else if (d.fail && t > 3) { stage = 'failed'; error = 'The tile pyramid could not be built. The file looks truncated or corrupted.'; }
  else if (t < 3) { stage = 'converting'; percent = Math.round(8 + 77 * (t / 3)); }
  else if (t < 5) { stage = 'ingesting'; percent = 90; }
  else {
    d.modality = d.modality || 'SM';
    stage = d.site ? 'published' : 'needs-details';
    if (stage === 'published' && !d.publishedAt) {
      d.publishedAt = Date.now();
      for (const c of DATA) for (const st of c.studies) if (st.public_id === d.study) st.unpublished--;
    }
  }
  // The Type chosen stands however the file was stored; only one never chosen is derived.
  const image_type = d.image_type ?? derivedType(d.modality, kind);
  return {
    image_id: d.image_id, name: d.name, kind, size: d.size, image_type, modality: d.modality ?? null, description: d.description ?? null, specimen_short_description: null,
    site: d.site ?? null, laterality: d.laterality ?? null, stain: d.stain ?? null, objective_power: d.objective_power ?? null, stage, percent, error,
    missing: [...(d.modality ? [] : ['modality']), ...(d.site ? [] : ['site'])], created_utc: new Date(d.created).toISOString(), expires_utc: stage === 'published' ? null : new Date(d.created + 864e5).toISOString(),
    upload: d.uploadId && !d.arrivedAt ? { id: d.uploadId, partSize: 5 * 1024 * 1024, partCount: Math.max(1, Math.ceil((d.size ?? 0) / (5 * 1024 * 1024))) } : null,
  };
}

// ── account and workspace ──
const ME = { givenName: 'Ana', familyName: 'Alvarez', title: 'Consultant pathologist' as string | null, institution: 'St. Catherine University Hospital' as string | null, linkedin: null as string | null, roleTitle: 'Pathologist' as string | null, prefs: {} as Record<string, boolean> };
const WORKSPACES = [
  { public_id: 'ws_1', name: 'Pathology Dept.', role: 'workspace_admin' as const, members: 6, is_default: true, archived: false, is_owner: true },
  { public_id: 'ws_2', name: 'Neuropath Lab', role: 'member' as const, members: 3, is_default: false, archived: false, is_owner: false },
  { public_id: 'ws_3', name: 'Teaching set', role: 'workspace_admin' as const, members: 1, is_default: false, archived: false, is_owner: true },
];
const PEOPLE = [
  { user_id: 'u1', email: 'ana.alvarez@hospital.org', display_name: 'Ana Alvarez', role: 'workspace_admin' as string, created_utc: ago(24 * 90), is_owner: false },
  { user_id: 'u2', email: 'marta.kern@hospital.org', display_name: 'Marta Kern', role: 'workspace_admin', created_utc: ago(24 * 80), is_owner: false },
  { user_id: 'u3', email: 'jonas.ruiz@hospital.org', display_name: 'Jonas Ruiz', role: 'member', created_utc: ago(24 * 60), is_owner: false },
  { user_id: 'u4', email: 'priya.natarajan@hospital.org', display_name: 'Priya Natarajan', role: 'member', created_utc: ago(24 * 30), is_owner: false },
  { user_id: 'u5', email: 'tom.adeyemi@hospital.org', display_name: 'Tom Adeyemi', role: 'workspace_admin', created_utc: ago(24 * 10), is_owner: false },
];
/** Each workspace's own copies of its people: roles and ownership are per workspace. */
const peopleOf = (ids: string[], owner: string, roles: Record<string, string> = {}) =>
  PEOPLE.filter((p) => ids.includes(p.user_id)).map((p) => ({ ...p, role: roles[p.user_id] ?? p.role, is_owner: p.user_id === owner }));
const MEMBERS = new Map<string, { members: typeof PEOPLE; invitations: Array<{ public_id: string; email: string; role: 'viewer' | 'member' | 'workspace_admin'; created_utc: string; expires_utc: string }> }>([
  ['ws_1', { members: peopleOf(['u1', 'u2', 'u3', 'u4', 'u5'], 'u1'), invitations: [{ public_id: 'inv_1', email: 'lena.fischer@hospital.org', role: 'member', created_utc: ago(30), expires_utc: ago(-24 * 6) }] }],
  ['ws_2', { members: peopleOf(['u1', 'u2', 'u3'], 'u2', { u1: 'member' }), invitations: [] }],
  ['ws_3', { members: peopleOf(['u1'], 'u1'), invitations: [] }],
]);
const DELETED = new Set<string>();
